// File: src/layout/Header/Header.js

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { auth } from '../../firebaseConfig';
import { signOut } from 'firebase/auth';

import 'react-toastify/dist/ReactToastify.css';
import './Header.css';

// Constants
const PROJECT_KEY = 'ijrr_user';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

/**
 * Header component for the application layout.
 * Navigation: Membership (always /membership), About Us, Jolly Roger Club, Jolly Roger Store, Maps, Member Login.
 */
export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef(null);
  const toggleRef = useRef(null);

  const [user, setUser] = useState(() => JSON.parse(localStorage.getItem(PROJECT_KEY)));
  const [pendingOrders, setPendingOrders] = useState(0);
  const [mainAssetThumb, setMainAssetThumb] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [validPhoto, setValidPhoto] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        localStorage.setItem(PROJECT_KEY, JSON.stringify(firebaseUser));
        fetchPendingOrders(firebaseUser.uid);
        fetchUserMainAsset(firebaseUser.uid);
      } else {
        setUser(null);
        setPendingOrders(0);
        setMainAssetThumb(null);
        localStorage.removeItem(PROJECT_KEY);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        toggleRef.current &&
        !toggleRef.current.contains(e.target)
      ) {
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const fetchPendingOrders = async (firebaseUid) => {
    try {
      const response = await fetch(`${API_BASE_URL}/customers/${firebaseUid}`);
      if (!response.ok) throw new Error('Failed to fetch pending orders');
      const data = await response.json();
      setPendingOrders(data.pending_orders_count || 0);
    } catch (error) {
      console.error('Error fetching pending orders:', error);
      setPendingOrders(0);
    }
  };

  const fetchUserMainAsset = async (firebaseUid) => {
    try {
      const response = await fetch(`${API_BASE_URL}/customer-assets/list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ firebase_uid: firebaseUid })
      });
      if (response.ok) {
        const data = await response.json();
        const mainAsset = data.assets.find(asset => asset.is_main);
        if (mainAsset) {
          setMainAssetThumb(mainAsset.thumbnail_url || mainAsset.url);
        } else {
          setMainAssetThumb(null);
        }
      }
    } catch (error) {
      console.error("Error fetching user assets:", error);
      setMainAssetThumb(null);
    }
  };

  const handleSignOut = async () => {
    await signOut(auth);
    localStorage.removeItem(PROJECT_KEY);
    setUser(null);
    setDropdownOpen(false);
    setPendingOrders(0);
    setMainAssetThumb(null);
    toast.info('You have been logged out.', { autoClose: 600 });
    setTimeout(() => navigate('/'), 1400);
  };

  const toggleDropdown = (e) => {
    e.preventDefault();
    setDropdownOpen((prev) => !prev);
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.charAt(0).toUpperCase();
  };

  const isActive = (paths) => {
    if (Array.isArray(paths)) {
      return paths.some((path) => location.pathname.startsWith(path));
    }
    return location.pathname.startsWith(paths);
  };

  const handleMobileToggle = () => {
    setIsMobileMenuOpen((prev) => !prev);
  };

  return (
    <header className="cdx-header">
      <ToastContainer />
      <div className="custom-container">
        <div className="row">
          <div className="col-12">
            <div className="header-contian">
              <div className="header-left">
                <div className="brand-logo">
                  <a href={user ? '/ijrr-gear' : '/'}>
                    <img className="img-fluid" src="/assets/images/ijrr/vertical-logo.png" alt="IJRR" />
                  </a>
                </div>
              </div>

              <div className="header-right" ref={menuRef}>
                <ul className={`codex-menu ${isMobileMenuOpen ? 'active' : ''}`}>
                  {/* 1. Membership - Now always directs to /membership */}
                  <li className={`menu-item ${isActive(['/membership', '/customize-membership', '/pricing']) ? 'active' : ''}`}>
                    <a href="/membership" onClick={() => setIsMobileMenuOpen(false)}>Membership</a>
                  </li>

                  {/* 2. About Us */}
                  <li className={`menu-item ${isActive('/about') ? 'active' : ''}`}>
                    <a href="/about" onClick={() => setIsMobileMenuOpen(false)}>About Us</a>
                  </li>

                  {/* 3. Jolly Roger Club */}
                  <li className={`menu-item ${isActive('/club') ? 'active' : ''}`}>
                    <a href="/club" onClick={() => setIsMobileMenuOpen(false)}>Jolly Roger Club</a>
                  </li>

                  {/* 4. Jolly Roger Store */}
                  <li className={`menu-item ${isActive('/ijrr-gear') ? 'active' : ''}`}>
                    <a href="/ijrr-gear" onClick={() => setIsMobileMenuOpen(false)}>Jolly Roger Store</a>
                  </li>

                  {/* 5. Maps */}
                  <li className={`menu-item ${isActive('/maps') ? 'active' : ''}`}>
                    <a href="/maps" onClick={() => setIsMobileMenuOpen(false)}>Maps</a>
                  </li>

                  {/* 6. Dashboard (Visible only for logged-in users) */}
                  {user && (
                    <li className={`menu-item ${isActive('/dashboard') ? 'active' : ''}`}>
                      <a href="/dashboard" onClick={() => setIsMobileMenuOpen(false)}>Dashboard</a>
                    </li>
                  )}

                  {/* 7. Member Login (Visible only for guests) */}
                  {!user && (
                    <li className={`menu-item ${isActive('/login') ? 'active' : ''}`}>
                      <a href="/login" onClick={() => setIsMobileMenuOpen(false)}>Member Login</a>
                    </li>
                  )}
                </ul>
              </div>

              <div className="nav-iconlist">
                {user && (
                  <ul>
                    <li>
                      <a href="/shopping-cart">
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M9 22C9.55228 22 10 21.5523 10 21C10 20.4477 9.55228 20 9 20C8.44772 20 8 20.4477 8 21C8 21.5523 8.44772 22 9 22Z" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
                          <path d="M20 22C20.5523 22 21 21.5523 21 21C21 20.4477 20.5523 20 20 20C19.4477 20 19 20.4477 19 21C19 21.5523 19.4477 22 20 22Z" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
                          <path d="M1 1H5L7.68 14.39C7.77144 14.8504 8.02191 15.264 8.38755 15.5583C8.75318 15.5583 9.2107 16.009 9.68 16H19.4C19.8693 16.009 20.3268 15.8526 20.6925 15.5583C21.0581 15.264 21.3086 14.8504 21.4 14.39L23 6H6" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
                        </svg>
                        {pendingOrders > 0 && <div className="nav-notification">{pendingOrders}</div>}
                      </a>
                    </li>

                    <li className="dropdownmenu" style={{ position: 'relative' }} onBlur={() => setTimeout(() => setDropdownOpen(false), 200)} tabIndex={0}>
                      <a className="dropdown-action" href="/" onClick={toggleDropdown}>
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
                          <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
                        </svg>
                      </a>
                      {dropdownOpen && (
                        <ul className="dropdownitem-list">
                          <li className="user-info" onClick={() => navigate('/profile')} style={{ cursor: 'pointer' }}>
                            {mainAssetThumb ? (
                                <img src={mainAssetThumb} alt="Main Asset" className="profile-pic" onError={() => setMainAssetThumb(null)} />
                            ) : (user && user.photoURL && validPhoto) ? (
                              <img src={user.photoURL} alt="Profile" className="profile-pic" onError={() => setValidPhoto(false)} />
                            ) : (
                              <div className="default-avatar">{getInitials(user?.displayName)}</div>
                            )}
                            <br />
                            <span className="user-name">{user?.displayName || 'User'}</span>
                            <br />
                            <small className="user-email">{user?.email}</small>
                          </li>
                          <li>
                            <button className="sign-out-btn" onClick={handleSignOut}>Sign Out</button>
                          </li>
                        </ul>
                      )}
                    </li>
                  </ul>
                )}

                <div className="mobilemenu-toggle" ref={toggleRef} onClick={handleMobileToggle}>
                  <i className="icofont-navigation-menu"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}